import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { useCurrency } from "../contexts/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { CreditCard, TrendingUp, Calendar, Receipt, FileText, Loader2, Wallet, CheckCircle } from "lucide-react";

var API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Billing() {
  var { user, token, checkAuth } = useAuth();
  var { t } = useTranslation();
  var { formatPrice } = useCurrency();
  var [searchParams, setSearchParams] = useSearchParams();
  var [billing, setBilling] = useState(null);
  var [loading, setLoading] = useState(true);
  var [paymentMethods, setPaymentMethods] = useState([]);
  var [monthlyPrice, setMonthlyPrice] = useState(0);
  var [priceCurrency, setPriceCurrency] = useState("eur");
  var [paying, setPaying] = useState(null);

  useEffect(function() {
    fetchBilling();
    fetchPaymentMethods();
  }, []);

  useEffect(function() {
    var paymentStatus = searchParams.get("payment");
    var sessionId = searchParams.get("session_id");
    if (paymentStatus === "success" && sessionId) {
      verifyStripePayment(sessionId);
    }
    var paypalSuccess = searchParams.get("paypal_success");
    var orderId = searchParams.get("order_id");
    var paypalOrderId = searchParams.get("paypal_order_id");
    if (paypalSuccess === "true" && orderId) {
      capturePaypalPayment(orderId, paypalOrderId);
    }
  }, [searchParams]);

  function fetchBilling() {
    setLoading(true);
    fetch(API_URL + "/api/my/billing", {
      credentials: "include",
      headers: token ? { Authorization: "Bearer " + token } : {}
    })
    .then(function(res) {
      if (res.ok) return res.json();
      throw new Error("Failed");
    })
    .then(function(data) {
      setBilling(data);
      setLoading(false);
    })
    .catch(function() {
      toast.error(t("common.error"));
      setLoading(false);
    });
  }

  function fetchPaymentMethods() {
    fetch(API_URL + "/api/payment-methods", {
      credentials: "include",
      headers: token ? { Authorization: "Bearer " + token } : {}
    })
    .then(function(res) {
      if (res.ok) return res.json();
      throw new Error("Failed");
    })
    .then(function(data) {
      setPaymentMethods(data.methods || []);
      setMonthlyPrice(data.monthly_price || 0);
      setPriceCurrency(data.currency || "eur");
    })
    .catch(function() {});
  }

  function verifyStripePayment(sessionId) {
    fetch(API_URL + "/api/payments/stripe/status/" + sessionId, {
      credentials: "include",
      headers: token ? { Authorization: "Bearer " + token } : {}
    })
    .then(function(res) { return res.json(); })
    .then(function(data) {
      if (data.payment_status === "paid") {
        toast.success(t("billing.paymentSuccess"));
        checkAuth();
        fetchBilling();
      } else {
        toast.error(t("billing.paymentFailed"));
      }
      setSearchParams({});
    })
    .catch(function() { setSearchParams({}); });
  }

  function capturePaypalPayment(orderId, paypalOrderId) {
    fetch(API_URL + "/api/payments/paypal/capture/" + orderId, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) },
      body: JSON.stringify({ paypal_order_id: paypalOrderId })
    })
    .then(function(res) { return res.json(); })
    .then(function(data) {
      if (data.status === "success" || data.status === "already_processed") {
        toast.success(t("billing.paymentSuccess"));
        checkAuth();
        fetchBilling();
      } else {
        toast.error(t("billing.paymentFailed"));
      }
      setSearchParams({});
    })
    .catch(function() { setSearchParams({}); });
  }

  function handlePayStripe() {
    setPaying("stripe");
    fetch(API_URL + "/api/payments/stripe/create-checkout", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) },
      body: JSON.stringify({
        user_id: user.user_id,
        origin_url: window.location.origin
      })
    })
    .then(function(res) {
      if (res.ok) return res.json();
      throw new Error("Failed");
    })
    .then(function(data) {
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(t("billing.checkoutError"));
        setPaying(null);
      }
    })
    .catch(function() {
      toast.error(t("billing.checkoutError"));
      setPaying(null);
    });
  }

  function handlePayPaypal() {
    setPaying("paypal");
    fetch(API_URL + "/api/payments/paypal/create-order", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) },
      body: JSON.stringify({
        user_id: user.user_id,
        origin_url: window.location.origin
      })
    })
    .then(function(res) {
      if (res.ok) return res.json();
      throw new Error("Failed");
    })
    .then(function(data) {
      if (data.approval_url) {
        window.location.href = data.approval_url;
      } else {
        toast.error(t("billing.checkoutError"));
        setPaying(null);
      }
    })
    .catch(function() {
      toast.error(t("billing.checkoutError"));
      setPaying(null);
    });
  }

  function handleViewInvoice(transactionId) {
    window.open(API_URL + "/api/my/billing/" + transactionId + "/invoice?token=" + token, "_blank");
  }

  function getStatusBadge(status) {
    var classes = {
      paid: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
      pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
      failed: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    };
    return <Badge className={classes[status] || ""}>{t("billing.statuses." + status, status)}</Badge>;
  }

  function getSubscriptionBadge(status) {
    var classes = {
      active: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400",
      trial: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      expired: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
      deactivated: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    };
    return <Badge className={classes[status] || ""}>{t("billing.subStatuses." + status, status)}</Badge>;
  }

  function formatDate(dateStr) {
    if (!dateStr) return "-";
    return dateStr.substring(0, 10);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  var sub = billing?.subscription || {};
  var transactions = billing?.transactions || [];
  var hasUnpaidTransactions = transactions.some(function(t) {
    return t.payment_status === "pending" || t.payment_status === "failed";
  });
  var showPayNow = hasUnpaidTransactions || sub.status === "expired" || sub.status === "deactivated";

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("billing.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("billing.subtitle")}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bento-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg"><CreditCard className="w-5 h-5 text-primary" /></div>
              <div>
                <p className="text-sm text-muted-foreground">{t("billing.subscription")}</p>
                <div className="mt-1">{getSubscriptionBadge(sub.status)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bento-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/10 rounded-lg"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
              <div>
                <p className="text-sm text-muted-foreground">{t("billing.totalSpent")}</p>
                <p className="text-xl font-bold" data-testid="total-spent-display">{formatPrice(billing?.total_spent || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bento-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg"><Calendar className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-sm text-muted-foreground">{t("billing.nextPayment")}</p>
                <p className="text-sm font-semibold" data-testid="next-payment-display">
                  {sub.next_payment_date ? formatDate(sub.next_payment_date) : "-"}
                  {sub.days_remaining !== null && sub.days_remaining !== undefined && (
                    <span className="text-muted-foreground font-normal ml-1">({sub.days_remaining}d)</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bento-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500/10 rounded-lg"><Receipt className="w-5 h-5 text-violet-600" /></div>
              <div>
                <p className="text-sm text-muted-foreground">{t("billing.invoiceCount")}</p>
                <p className="text-xl font-bold" data-testid="invoice-count-display">{billing?.transaction_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {showPayNow && paymentMethods.length > 0 && (
        <Card className="bento-card border-primary/20" data-testid="pay-now-section">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Wallet className="w-5 h-5" />
              {t("billing.payNow")}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {hasUnpaidTransactions
                ? t("billing.payOpenInvoiceDesc", { price: formatPrice(monthlyPrice) })
                : t("billing.payNowDesc", { price: formatPrice(monthlyPrice) })}
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {paymentMethods.map(function(method) {
                var isLoading = paying === method.id;
                return (
                  <Button
                    key={method.id}
                    variant={method.id === "stripe" ? "default" : "outline"}
                    className="min-w-[180px]"
                    disabled={paying !== null}
                    onClick={method.id === "stripe" ? handlePayStripe : handlePayPaypal}
                    data-testid={"pay-btn-" + method.id}
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : method.id === "stripe" ? (
                      <CreditCard className="w-4 h-4 mr-2" />
                    ) : (
                      <Wallet className="w-4 h-4 mr-2" />
                    )}
                    {t("billing.payWith", { method: method.name })}
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {sub.status === "active" && !showPayNow && (
        <Card className="bento-card border-emerald-200 dark:border-emerald-900/30">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">{t("billing.subscriptionActive")}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div>
        <h2 className="text-lg font-semibold mb-4">{t("billing.history")}</h2>
        {transactions.length > 0 ? (
          <Card className="bento-card overflow-hidden">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("billing.date")}</TableHead>
                  <TableHead>{t("billing.description")}</TableHead>
                  <TableHead>{t("billing.method")}</TableHead>
                  <TableHead>{t("billing.amount")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead className="w-20">{t("billing.invoice")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map(function(txn) {
                  return (
                    <TableRow key={txn.transaction_id} data-testid={"txn-row-" + txn.transaction_id}>
                      <TableCell>{formatDate(txn.paid_at || txn.created_at)}</TableCell>
                      <TableCell className="font-medium">{t("billing.monthlySubscription")}</TableCell>
                      <TableCell>
                        <span className="capitalize">{txn.payment_method === "stripe" ? "Stripe" : txn.payment_method === "paypal" ? "PayPal" : txn.payment_method}</span>
                      </TableCell>
                      <TableCell>{formatPrice(txn.amount)}</TableCell>
                      <TableCell>{getStatusBadge(txn.payment_status)}</TableCell>
                      <TableCell>
                        {txn.payment_status === "paid" && (
                          <Button variant="ghost" size="icon" onClick={function() { handleViewInvoice(txn.transaction_id); }} title={t("billing.downloadInvoice")} data-testid={"invoice-btn-" + txn.transaction_id}>
                            <FileText className="w-4 h-4 text-blue-600" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <Card className="bento-card">
            <CardContent className="py-12 text-center text-muted-foreground">
              <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{t("billing.noTransactions")}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
